import React, { useState, useEffect } from 'react';
import { Smartphone, Activity, Target, AlertCircle, CheckCircle, TrendingUp, Calendar, Zap } from 'lucide-react';
import ProgressBar from '../common/ProgressBar';
import StatCard from '../common/StatCard';
import InputCard from '../common/InputCard';
import TipCard from '../common/TipCard';
import { motion } from 'framer-motion';

const MobileDataForecast = () => {
  const [usedGB, setUsedGB] = useState(10);
  const [daysPassed, setDaysPassed] = useState(15);
  const [totalDays, setTotalDays] = useState(30);
  const [dailyAvg, setDailyAvg] = useState(0);
  const [predictedTotal, setPredictedTotal] = useState(0);
  const [remainingNeeded, setRemainingNeeded] = useState(0);
  const [willExceed, setWillExceed] = useState(false);

  useEffect(() => {
    const avg = usedGB / daysPassed;
    const predicted = avg * totalDays;
    const remaining = predicted - usedGB;
    const remainingDays = totalDays - daysPassed;
    
    setDailyAvg(Math.round(avg * 10) / 10);
    setPredictedTotal(Math.round(predicted * 10) / 10);
    setRemainingNeeded(Math.round(remaining * 10) / 10);
    setWillExceed(remaining > (remainingDays * 5));
  }, [usedGB, daysPassed, totalDays]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InputCard title="Data Usage Inputs" icon={Smartphone} delay={0.1}>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Total Data Used (GB)</label>
              <input 
                type="number" 
                value={usedGB} 
                onChange={(e) => setUsedGB(parseFloat(e.target.value) || 0)} 
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Days Passed in Cycle</label>
              <input 
                type="range" 
                min="1" 
                max={totalDays - 1} 
                value={daysPassed} 
                onChange={(e) => setDaysPassed(parseInt(e.target.value))} 
                className="w-full"
              />
              <div className="flex justify-between text-sm text-gray-500 mt-1">
                <span>Day 1</span>
                <span className="font-medium text-blue-600">{daysPassed} days</span>
                <span>Day {totalDays}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Total Days in Billing Cycle</label>
              <select 
                value={totalDays} 
                onChange={(e) => setTotalDays(parseInt(e.target.value))} 
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="28">28 days</option>
                <option value="30">30 days</option>
                <option value="31">31 days</option>
              </select>
            </div>
          </div>
        </InputCard>

        <div className="space-y-5">
          <StatCard title="Daily Average Usage" value={dailyAvg} unit="GB/day" icon={Activity} color="bg-blue-500" delay={0.2} />
          <StatCard title="Predicted Total" value={predictedTotal} unit="GB" icon={Target} color="bg-purple-500" delay={0.3} />
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm p-6 border border-gray-100"
      >
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <TrendingUp size={18} className="text-blue-500" />
          Usage Forecast Overview
        </h3>
        <div className="space-y-4">
          <ProgressBar 
            value={usedGB} 
            max={predictedTotal} 
            color={willExceed ? "red" : "green"} 
            label="Used so far" 
            sublabel={`${usedGB} GB / ${predictedTotal} GB`} 
          />
          <ProgressBar 
            value={remainingNeeded > 0 ? remainingNeeded : 0} 
            max={predictedTotal} 
            color="orange" 
            label="Remaining data needed" 
            sublabel={`${remainingNeeded > 0 ? remainingNeeded : 0} GB`} 
          />
        </div>
        
        {willExceed && (
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mt-5 p-4 bg-red-50 rounded-xl flex items-center gap-3 border border-red-200"
          >
            <AlertCircle size={22} className="text-red-600" />
            <div>
              <p className="font-semibold text-red-700">⚠️ Warning: You may exceed your data plan!</p>
              <p className="text-sm text-red-600">At current rate, you'll need {remainingNeeded} GB more over {totalDays - daysPassed} days.</p>
            </div>
          </motion.div>
        )}
        
        {!willExceed && predictedTotal > 0 && (
          <motion.div 
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mt-5 p-4 bg-green-50 rounded-xl flex items-center gap-3 border border-green-200"
          >
            <CheckCircle size={22} className="text-green-600" />
            <div>
              <p className="font-semibold text-green-700">✅ On track! Keep up the good usage pattern.</p>
              <p className="text-sm text-green-600">You're using data efficiently this month.</p>
            </div>
          </motion.div>
        )}
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TipCard icon={Calendar} title="Pro Tip" text="Track your usage weekly to avoid overages" color="blue" />
        <TipCard icon={Zap} title="Save Data" text="Use WiFi when available for large downloads" color="green" />
      </div>
    </motion.div>
  );
};

export default MobileDataForecast;